import React, { useState, useEffect } from 'react';
import { Button, Select, Space, message, Tooltip, Divider } from 'antd';
import {
  PlayCircleOutlined,
  SaveOutlined,
  UndoOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  CopyOutlined,
  DownloadOutlined,
} from '@ant-design/icons';

const { Option } = Select;

const SimpleCodeEditor = ({
  initialCode = '// 请在此处编写代码\n',
  language = 'javascript',
  theme = 'vs-dark',
  height = '500px',
  onRun,
  onSave,
  readOnly = false,
  options = {}
}) => {
  const [code, setCode] = useState(initialCode);
  const [currentLanguage, setCurrentLanguage] = useState(language);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  // 支持的编程语言
  const languages = [
    { value: 'javascript', label: 'JavaScript' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'cpp', label: 'C++' },
    { value: 'csharp', label: 'C#' },
  ];

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isFullscreen]);

  const handleLanguageChange = (value) => {
    setCurrentLanguage(value);
  };

  const handleCodeChange = (e) => {
    setCode(e.target.value);
  };

  const handleRun = () => {
    if (isRunning) return;
    
    setIsRunning(true);
    try {
      onRun && onRun(code, currentLanguage);
    } catch (error) {
      message.error('代码执行失败');
      console.error(error);
    } finally {
      setTimeout(() => {
        setIsRunning(false);
      }, 1000);
    }
  };

  const handleSave = () => {
    onSave && onSave(code, currentLanguage);
    message.success('代码已保存');
  };

  const handleRestore = () => {
    setCode(initialCode);
    message.info('代码已重置');
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code)
      .then(() => message.success('代码已复制到剪贴板'))
      .catch(() => message.error('复制失败，请手动复制'));
  };

  const handleDownload = () => {
    const fileExtensions = {
      javascript: 'js',
      python: 'py',
      java: 'java',
      cpp: 'cpp',
      csharp: 'cs'
    };
    
    const extension = fileExtensions[currentLanguage] || 'txt';
    const filename = `code.${extension}`;
    const blob = new Blob([code], { type: 'text/plain' });
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    
    URL.revokeObjectURL(link.href);
    message.success(`代码已下载为 ${filename}`);
  };

  // 根据主题设置样式
  const getThemeStyles = () => {
    if (theme === 'vs-dark' || theme === 'hc-black') {
      return {
        backgroundColor: '#1e1e1e',
        color: '#d4d4d4',
        border: '1px solid #333'
      };
    }
    return {
      backgroundColor: '#ffffff',
      color: '#000000',
      border: '1px solid #ccc'
    };
  };

  return (
    <div
      style={{
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        right: isFullscreen ? 0 : 'auto',
        bottom: isFullscreen ? 0 : 'auto',
        zIndex: isFullscreen ? 1000 : 'auto',
        background: theme === 'vs-dark' ? '#1e1e1e' : '#f5f5f5',
        padding: '10px',
        borderRadius: isFullscreen ? 0 : '4px',
        display: 'flex',
        flexDirection: 'column',
        height: isFullscreen ? '100vh' : height,
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
        backgroundColor: theme === 'vs-dark' ? '#252526' : '#e6e6e6',
        padding: '8px 16px',
        borderRadius: '4px'
      }}>
        <Space>
          <Select
            value={currentLanguage}
            onChange={handleLanguageChange}
            style={{ width: 120 }}
            disabled={readOnly}
          >
            {languages.map(lang => (
              <Option key={lang.value} value={lang.value}>{lang.label}</Option>
            ))}
          </Select>
        </Space>

        <Space>
          {onRun && (
            <Tooltip title="运行代码">
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleRun}
                loading={isRunning}
                disabled={readOnly}
              >
                运行
              </Button>
            </Tooltip>
          )}

          {onSave && (
            <Tooltip title="保存代码">
              <Button
                icon={<SaveOutlined />}
                onClick={handleSave}
                disabled={readOnly}
              >
                保存
              </Button>
            </Tooltip>
          )}

          <Tooltip title="重置代码">
            <Button
              icon={<UndoOutlined />}
              onClick={handleRestore}
              disabled={readOnly}
            >
              重置
            </Button>
          </Tooltip>

          <Divider type="vertical" />

          <Tooltip title="复制代码">
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopyCode}
            />
          </Tooltip>

          <Tooltip title="下载代码">
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownload}
            />
          </Tooltip>

          <Tooltip title={isFullscreen ? "退出全屏" : "全屏编辑"}>
            <Button
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={handleToggleFullscreen}
            />
          </Tooltip>
        </Space>
      </div>

      <textarea
        value={code}
        onChange={handleCodeChange}
        style={{
          ...getThemeStyles(),
          width: '100%',
          height: '100%',
          flexGrow: 1,
          padding: '10px',
          fontFamily: 'Consolas, "Courier New", monospace',
          fontSize: '14px',
          lineHeight: '1.5',
          resize: 'none',
          outline: 'none',
          tabSize: 2,
        }}
        spellCheck="false"
        autoCapitalize="off"
        autoComplete="off"
        autoCorrect="off"
        readOnly={readOnly}
      />
    </div>
  );
};

export default SimpleCodeEditor;
